"use client";

import React, {type ForwardedRef, useEffect, useState, useRef} from "react";
import Tippy from "@tippyjs/react/headless";
import "tippy.js/dist/tippy.css";

import {cn, useMergeRefs} from "@isudio/utils";

import {Label, useTippyRegistry} from "../";

type TextAreaProps =
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
  & {
  rows?: number;
  cols?: number;
  error?: string;
  display?: true | false;
  roColor?: string;
  showToast?: (message: string) => void;
};

const TextAreaValidator = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({
     className,
     minLength,
     maxLength,
     rows = 3,
     cols = 20,
     error,
     display = true,
     roColor = "text-custom-gray-300",
     onChange,
     onBlur,
     showToast,
     ...props
   },
   ref: ForwardedRef<HTMLTextAreaElement>
  ) => {
    const {
      register,
      unregister
    } = useTippyRegistry();

    const internalRef = useRef<HTMLTextAreaElement | null>(null);
    const initialVal = props.value ?? props.defaultValue ?? (internalRef.current?.value ?? "");
    const [hasFocus, setHasFocus] = useState(Boolean(false));
    const [hasValue, setHasValue] = useState(Boolean(String(initialVal).trim()));
    const [showError, setShowError] = useState(false);
    const [hovering, setHovering] = useState(false);
    const hideTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearHideTimer = () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };

    const startHideTimer = (delay = 2000) => {
      clearHideTimer();
      hideTimerRef.current = setTimeout(() => {
        setShowError(false);
        hideTimerRef.current = null;
      }, delay);
    };

    useEffect(() => {
      const domVal = internalRef.current?.value;
      const v = props.value ?? props.defaultValue ?? domVal ?? "";
      setHasValue(Boolean(String(v).trim()));
    }, [props.value, props.defaultValue]);

    useEffect(() => {
      if (error && !hovering) {
        setShowError(true);
        startHideTimer();
      } else if (!error) {
        setShowError(false);
        clearHideTimer();
      }
      return clearHideTimer;
    }, [error]);

    const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setHasFocus(true);
      props.onFocus?.(e);
    }

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setHasFocus(false);
      onBlur?.(e);
      if (error) {
        setShowError(true);
        startHideTimer();
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setHasValue(Boolean(String(e.target.value).trim()));
      onChange?.(e);
    }

    const handleMouseEnter = () => {
      if (!error) return;
      clearHideTimer();
      setHovering(true);
      setShowError(true);
    };

    const handleMouseLeave = () => {
      setHovering(false);
      setShowError(false);
      clearHideTimer();
    };

    const inputEl = (
      <div
        className="relative w-full flex gap-2 justify-center items-center">

        <div
          className={cn(
            "relative w-full flex p-2 gap-2 justify-center items-center border-input-field",
            hasFocus ? "border-input-field__focus" : "",
            error ? "border-input-field__error" : "",
            display ? "" : "hidden",
            "rounded-[20px]!"
          )}>
          <textarea
            {...props}
            ref={useMergeRefs(internalRef, ref, (props as any).ref)}
            className={cn(
              "flex w-full h-16 p-0 leading-[1.6] bg-transparent border-none resize-none text-base tracking-tight focus-visible:outline-none focus-visible:ring-0 placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
              props.readOnly ? roColor : "",
              className
            )}
            placeholder={props.placeholder}
            rows={rows}
            cols={cols}
            minLength={minLength}
            maxLength={maxLength}
            spellCheck="false"
            onFocus={handleFocus}
            onChange={handleChange}
            onBlur={handleBlur}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
        </div>
        {display && hasValue && (
          <Label className="absolute -top-2 left-2.25 text-[10px] font-semibold text-generic-dull">{props.placeholder}</Label>
        )}
      </div>
    );

    return (
      <Tippy
        onCreate={instance => register(instance)}
        onDestroy={instance => unregister(instance)}
        className={cn(
          "err-tooltip",
          display ? "" : "hidden",
        )}
        placement="bottom"
        visible={!!error && showError}
        interactive={false}
        hideOnClick={true}
        delay={0}
        render={(attrs) => (
          <div
            {...attrs}
            tabIndex={-1}
            className={cn(
              "err-tooltip-box box bg-[#640a0a] text-white text-sm px-2 py-1 rounded shadow whitespace-pre-line",
              display ? "" : "hidden",
            )}
          >
            {error ?? ""}
            <div
              data-popper-arrow
              className="err-tooltip-arrow bg-[#640a0a]! w-3! h-3! rotate-45!"
            />
          </div>
        )}
      >
        {inputEl}
      </Tippy>
    );
  }
);

TextAreaValidator.displayName = "TextAreaValidator";
export {TextAreaValidator};

